/**
 * Spectrogram.tsx
 * =============================================================================
 * Liquid Wave Visualization Component
 * Renders organic multi-harmonic waves using Canvas API
 * 
 * @author Neuro-Audio Lab
 * @version 1.0.0
 */

import { useEffect, useRef, useState } from 'react';

// =============================================================================
// TYPES
// =============================================================================

interface SpectrogramProps {
  isActive: boolean;  // Controls wave amplitude (active vs. idle state)
  modeId: string;     // 'focus', 'sleep', 'anxiety' (or similar IDs)
  color: string;      // Primary color in Hex/RGB format
}

interface WaveParams {
  frequency: number;   // Wave density (higher = more waves per width)
  amplitude: number;   // Wave height
  speed: number;       // Animation velocity
}

// =============================================================================
// WAVE PARAMETER MAPPING
// =============================================================================

const WAVE_CONFIGS: Record<string, WaveParams> = {
  focus: {
    frequency: 0.02,    // High frequency (tight waves) - Beta/Gamma simulation
    amplitude: 20,      // Medium amplitude
    speed: 0.04,        // Medium-fast speed
  },
  sleep: {
    frequency: 0.008,   // Very low frequency (wide waves) - Delta/Theta simulation
    amplitude: 25,      // Reduced amplitude to prevent clipping
    speed: 0.015,       // Very slow speed
  },
  anxiety: {
    frequency: 0.015,   // Balanced frequency - Alpha simulation
    amplitude: 25,      // Balanced amplitude
    speed: 0.03,        // Smooth, organic movement
  },
};

// Fallback configuration
const DEFAULT_CONFIG: WaveParams = WAVE_CONFIGS.anxiety;

// =============================================================================
// COMPONENT
// =============================================================================

export default function Spectrogram({ 
  isActive, 
  modeId, 
  color 
}: SpectrogramProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const timeRef = useRef<number>(0);
  
  // Smooth amplitude transition state
  const [currentAmplitude, setCurrentAmplitude] = useState<number>(1);
  const targetAmplitudeRef = useRef<number>(isActive ? 1 : 0.05);

  // =============================================================================
  // WAVE RENDERING LOGIC
  // =============================================================================

  const drawWave = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    waveColor: string,
    speed: number,
    amplitude: number,
    frequency: number,
    yOffset: number,
    time: number
  ) => {
    ctx.beginPath();
    ctx.strokeStyle = waveColor;
    ctx.lineWidth = 1.5;

    for (let x = 0; x < width; x++) {
      // Base wave
      let y = Math.sin(x * frequency + time * speed) * amplitude;
      
      // Harmonic (adds complexity)
      y += Math.sin(x * frequency * 2 + time * speed * 1.5) * (amplitude / 2);
      
      // Sub-harmonic (adds depth)
      y += Math.sin(x * frequency * 0.5 + time * speed * 0.5) * (amplitude / 2);

      ctx.lineTo(x, height / 2 + y + yOffset);
    }

    ctx.stroke();
  };

  // =============================================================================
  // ANIMATION LOOP
  // =============================================================================

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Get wave parameters based on mode
    const config = WAVE_CONFIGS[modeId] || DEFAULT_CONFIG;

    // Resize handler with Retina support
    const handleResize = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      
      // Retina display support (2x scaling)
      canvas.width = width * 2;
      canvas.height = height * 2;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(2, 2);
    };

    // Initial resize
    handleResize();

    // Animation function
    const animate = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Smooth amplitude interpolation (easing)
      const amplitudeDelta = targetAmplitudeRef.current - currentAmplitude;
      if (Math.abs(amplitudeDelta) > 0.001) {
        setCurrentAmplitude(prev => prev + amplitudeDelta * 0.1);
      }

      // Background wave (darker, slower)
      const bgColor = adjustColorOpacity(color, 0.3);
      drawWave(
        ctx,
        width,
        height,
        bgColor,
        config.speed * 0.6,
        config.amplitude * 1.6 * currentAmplitude,
        config.frequency * 0.7,
        0,
        timeRef.current
      );

      // Main wave
      drawWave(
        ctx,
        width,
        height,
        color,
        config.speed,
        config.amplitude * currentAmplitude,
        config.frequency,
        0,
        timeRef.current
      );

      timeRef.current += 1;
      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animate();

    // Setup ResizeObserver
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [modeId, color, currentAmplitude]);

  // =============================================================================
  // UPDATE TARGET AMPLITUDE WHEN isActive CHANGES
  // =============================================================================

  useEffect(() => {
    targetAmplitudeRef.current = isActive ? 1 : 0.05; // Near-flat when inactive
  }, [isActive]);

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full overflow-hidden"
      style={{
        // Fade mask effect (critical for aesthetics)
        maskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)',
      }}
    >
      <canvas 
        ref={canvasRef}
        className="absolute inset-0"
      />
    </div>
  );
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Adjusts color opacity for background wave
 * Handles both hex and rgb(a) formats
 */
function adjustColorOpacity(color: string, opacity: number): string {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  
  // Handle rgb/rgba colors
  if (color.startsWith('rgb')) {
    const values = color.match(/\d+/g);
    if (values && values.length >= 3) {
      return `rgba(${values[0]}, ${values[1]}, ${values[2]}, ${opacity})`;
    }
  }
  
  // Fallback to cyan with opacity
  return `rgba(34, 211, 238, ${opacity})`;
}